import { ZOOM_RESCALING_FACTOR } from '@/constants/map';
import { mapDataInitialPositionSelector, mapDataSizeSelector } from '@/redux/map/map.selectors';
import { usePointToProjection } from '@/utils/map/usePointToProjection';
import { View } from 'ol';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  getMapExtent,
  getMapViewConfig,
} from '@/components/Map/MapViewer/utils/config/getMapViewConfig';

/**
 * Builds a read-only "follower" {@link View} for the magnifier loupe.
 *
 * It shares the main map's extent and zoom scaling (so the resolution-based
 * element cutoff stays consistent — see `isFeatureFilled`) but is fixed at the
 * maximum zoom to reveal detail that the main map culls when zoomed out. The
 * loupe component drives `view.setCenter(...)` imperatively from the cursor
 * position; this view never reads or writes the Redux map position.
 */
export const useOlMapLoupeView = (): View => {
  const mapSize = useSelector(mapDataSizeSelector);
  const mapInitialPosition = useSelector(mapDataInitialPositionSelector);
  const pointToProjection = usePointToProjection();

  const extent = useMemo(
    () => getMapExtent(mapSize, pointToProjection),
    [mapSize, pointToProjection],
  );

  const center = useMemo(
    () => pointToProjection({ x: mapInitialPosition.x, y: mapInitialPosition.y }),
    [mapInitialPosition.x, mapInitialPosition.y, pointToProjection],
  );

  const viewConfig = useMemo(
    () =>
      getMapViewConfig({
        mapSize,
        center,
        zoom: mapSize.maxZoom * ZOOM_RESCALING_FACTOR,
        extent,
      }),
    [mapSize, center, extent],
  );

  // The actual render resolution is driven imperatively by the loupe component
  // (relative to the primary map's max-zoom resolution), so the loupe shows the
  // widest field of view that still renders full detail. Here we only build the
  // view with the shared extent and zoom scaling.
  return useMemo(() => new View(viewConfig), [viewConfig]);
};
